import {
  CheckCircleIcon,
  CreditCardIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import clsx from "clsx";
import { useState } from "react";
import { z } from "zod";

import { useBillingSummaryQuery } from "@/api/billing/useBillingSummaryQuery";
import { useStorageStatsQuery } from "@/api/billing/useStorageStatsQuery";
import { useSubscriptionCheckoutMutation } from "@/api/billing/useSubscriptionCheckoutMutation";
import { useSubscriptionPortalMutation } from "@/api/billing/useSubscriptionPortalMutation";
import { useSubscriptionQuery } from "@/api/billing/useSubscriptionQuery";
import { useTopupMutation } from "@/api/billing/useTopupMutation";
import { useUsageLimitsQuery } from "@/api/billing/useUsageLimitsQuery";
import SkeletonLine from "@/components/feedback/SkeletonLine";
import toast from "@/components/feedback/toast";
import Button from "@/components/form/Button";
import { BasicLayout } from "@/components/layout/BasicLayout";
import { useWorkspaceStore } from "@/stores/workspace.store";

const billingSearchSchema = z.object({
  success: z.coerce.boolean().optional().catch(undefined),
  canceled: z.coerce.boolean().optional().catch(undefined),
});

export const Route = createFileRoute("/_auth/billing")({
  component: BillingPage,
  validateSearch: billingSearchSchema,
});

const TOPUP_OPTIONS = [10, 25, 50, 100];

function formatCents(cents: number): string {
  return (cents / 100).toFixed(2);
}

function formatStorageSize(mb: number): string {
  if (mb >= 1024) {
    return `${(mb / 1024).toFixed(1)} GB`;
  }
  return `${mb.toFixed(1)} MB`;
}

/* ─── Glass Card ─── */
function GlassCard({
  children,
  className,
  span,
  glow,
}: {
  children: React.ReactNode;
  className?: string;
  span?: boolean;
  glow?: { color: string; position: string };
}) {
  return (
    <div
      className={clsx(
        "relative overflow-hidden rounded-2xl p-5 sm:p-6",
        "bg-surface/60 backdrop-blur-xl border border-border",
        "hover:border-border-hover transition-all duration-200",
        span && "lg:col-span-2",
        className,
      )}
    >
      {glow && (
        <div
          className="absolute w-[200px] h-[200px] rounded-full pointer-events-none blur-[80px]"
          style={{ background: glow.color, ...parsePosition(glow.position) }}
        />
      )}
      <div className="relative">{children}</div>
    </div>
  );
}

function parsePosition(pos: string): React.CSSProperties {
  const map: Record<string, React.CSSProperties> = {
    "top-right": { top: -60, right: -40 },
    "bottom-left": { bottom: -40, left: -40 },
    "top-left": { top: -40, left: -40 },
    "bottom-right": { bottom: -40, right: -40 },
  };
  return map[pos] ?? map["top-right"];
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] font-medium uppercase tracking-[1.5px] text-tertiary mb-4">
      {children}
    </p>
  );
}

/* ─── Circular Progress ─── */
function CircularProgress({
  pct,
  isHigh,
}: {
  pct: number;
  isHigh: boolean;
}) {
  const r = 23;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(pct, 100) / 100) * circ;
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" className="shrink-0">
      <circle
        cx="28"
        cy="28"
        r={r}
        fill="none"
        strokeWidth="3"
        className="stroke-surface-hover"
      />
      <circle
        cx="28"
        cy="28"
        r={r}
        fill="none"
        strokeWidth="3"
        className={isHigh ? "stroke-red-500" : "stroke-accent"}
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        transform="rotate(-90 28 28)"
        style={{ transition: "stroke-dashoffset 0.5s ease" }}
      />
    </svg>
  );
}

/* ─── Thin progress bar with glow ─── */
function ProgressBar({ pct, isHigh }: { pct: number; isHigh: boolean }) {
  return (
    <div className="h-1 bg-surface-hover rounded-full overflow-hidden">
      <div
        className={clsx(
          "h-full rounded-full transition-all",
          isHigh
            ? "bg-red-500"
            : "bg-gradient-to-r from-accent to-blue-400 shadow-glow",
        )}
        style={{
          width: `${pct}%`,
          background: isHigh
            ? undefined
            : "linear-gradient(90deg, var(--accent), #60a5fa)",
          boxShadow: isHigh
            ? undefined
            : "0 0 12px rgba(59,130,246,0.3)",
        }}
      />
    </div>
  );
}

/* ─── Main ─── */
function BillingPage() {
  const workspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);
  const { success, canceled } = useSearch({ from: "/_auth/billing" });

  const {
    data,
    isPending,
    isError: isBillingError,
  } = useBillingSummaryQuery(workspaceId ?? "");
  const {
    data: storageData,
    isPending: isStoragePending,
    isError: isStorageError,
  } = useStorageStatsQuery(workspaceId ?? "");
  const {
    data: usageData,
    isPending: isUsagePending,
    isError: isUsageError,
  } = useUsageLimitsQuery(workspaceId ?? "");
  const {
    data: subData,
    isPending: isSubPending,
    isError: isSubError,
  } = useSubscriptionQuery(workspaceId ?? "");
  const topupMutation = useTopupMutation();
  const portalMutation = useSubscriptionPortalMutation();
  const checkoutMutation = useSubscriptionCheckoutMutation();

  const [selectedAmount, setSelectedAmount] = useState<number>(25);

  const handleTopup = async () => {
    if (!workspaceId) return;
    try {
      const result = await topupMutation.mutateAsync({
        workspaceId,
        amountDollars: selectedAmount,
      });
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      }
    } catch {
      toast({ message: "Operacja nie powiodła się.", type: "error" });
    }
  };

  const hasStripeSubscription =
    subData?.subscription?.hasStripeSubscription ?? false;

  const handleCheckout = async () => {
    if (!workspaceId || !subData?.subscription?.plan?.slug) return;
    try {
      const result = await checkoutMutation.mutateAsync({
        workspaceId,
        planSlug: subData.subscription.plan.slug,
      });
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      }
    } catch {
      toast({
        message: "Nie udało się utworzyć sesji płatności.",
        type: "error",
      });
    }
  };

  const balanceDollars = data ? formatCents(data.balanceCents) : "0.00";
  const storageLimitMB = subData?.subscription?.limits?.maxStorageMB ?? null;

  const dailyPct =
    usageData && usageData.limits.daily > 0
      ? Math.min((usageData.usage.daily / usageData.limits.daily) * 100, 100)
      : 0;
  const weeklyPct =
    usageData && usageData.limits.weekly > 0
      ? Math.min(
          (usageData.usage.weekly / usageData.limits.weekly) * 100,
          100,
        )
      : 0;
  const storagePct =
    storageData && storageLimitMB
      ? Math.min(
          (storageData.storage.total.totalMB / storageLimitMB) * 100,
          100,
        )
      : 0;

  return (
    <BasicLayout title="Billing">
      <div className="max-w-[680px] mx-auto px-4 sm:px-6 py-8 sm:py-12 h-full overflow-y-auto">
        {/* ─── Banners ─── */}
        {success && (
          <div className="mb-6 p-3 bg-green-500/10 border border-green-500/20 rounded-2xl flex items-center gap-3">
            <CheckCircleIcon className="size-5 text-green-500 shrink-0" />
            <p className="text-sm text-green-700">
              Doładowanie udane — środki dodane do konta.
            </p>
          </div>
        )}
        {canceled && (
          <div className="mb-6 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-2xl flex items-center gap-3">
            <XCircleIcon className="size-5 text-yellow-500 shrink-0" />
            <p className="text-sm text-yellow-700">
              Płatność anulowana. Możesz spróbować ponownie.
            </p>
          </div>
        )}

        {/* ─── Bento Grid ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* ═══ Balance ═══ */}
          <GlassCard
            glow={{
              color: "rgba(59,130,246,0.12)",
              position: "top-right",
            }}
            className="min-h-[160px] flex flex-col justify-between"
          >
            {isPending ? (
              <>
                <SkeletonLine className="h-3 w-20 mb-6" />
                <SkeletonLine className="h-12 w-40" />
              </>
            ) : isBillingError ? (
              <p className="text-sm text-red-500">
                Nie udało się pobrać salda.
              </p>
            ) : (
              <>
                <SectionLabel>Saldo konta</SectionLabel>
                <div>
                  <span className="text-[40px] font-bold text-primary tabular-nums tracking-tight leading-none">
                    ${balanceDollars}
                  </span>
                  <p className="text-[10px] text-tertiary uppercase tracking-[1.5px] mt-1.5">
                    USD
                  </p>
                </div>
              </>
            )}
          </GlassCard>

          {/* ═══ Subscription ═══ */}
          <GlassCard
            glow={{
              color: "rgba(34,197,94,0.08)",
              position: "bottom-left",
            }}
            className="min-h-[160px]"
          >
            {isSubPending ? (
              <>
                <SkeletonLine className="h-3 w-24 mb-6" />
                <SkeletonLine className="h-8 w-40 mb-3" />
                <SkeletonLine className="h-4 w-32" />
              </>
            ) : isSubError ? (
              <p className="text-sm text-red-500">
                Nie udało się pobrać danych subskrypcji.
              </p>
            ) : subData?.subscription ? (
              <>
                <div className="flex items-start justify-between mb-3">
                  <SectionLabel>Subskrypcja</SectionLabel>
                  <span
                    className={clsx(
                      "inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium",
                      subData.subscription.status === "active" &&
                        "bg-green-500/10 text-green-500",
                      subData.subscription.status === "past_due" &&
                        "bg-yellow-500/10 text-yellow-500",
                      subData.subscription.status === "canceled" &&
                        "bg-red-500/10 text-red-500",
                      subData.subscription.status === "incomplete" &&
                        "bg-surface-hover text-tertiary",
                    )}
                  >
                    {subData.subscription.status === "active" && "Aktywna"}
                    {subData.subscription.status === "past_due" && "Zaległa"}
                    {subData.subscription.status === "canceled" && "Anulowana"}
                    {subData.subscription.status === "incomplete" && "Niekompletna"}
                  </span>
                </div>

                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-[22px] font-bold text-primary">
                    {subData.subscription.plan.name}
                  </span>
                </div>
                <p className="text-xs text-tertiary">
                  ${formatCents(subData.subscription.effectivePrice)}/mies.
                  {subData.subscription.currentPeriodEnd && (
                    <> · Odnowienie{" "}
                      {new Date(
                        subData.subscription.currentPeriodEnd,
                      ).toLocaleDateString("pl-PL")}
                    </>
                  )}
                </p>

                {subData.subscription.cancelAtPeriodEnd && (
                  <p className="mt-3 text-xs text-yellow-600 bg-yellow-500/10 rounded-xl px-3 py-2">
                    Anulowana na koniec okresu.
                  </p>
                )}

                <div className="mt-4">
                  {!hasStripeSubscription ? (
                    <Button
                      size="xs"
                      isLoading={checkoutMutation.isPending}
                      onClick={handleCheckout}
                    >
                      Opłać — ${formatCents(subData.subscription.effectivePrice)}/mies.
                    </Button>
                  ) : (
                    subData.subscription.status !== "canceled" && (
                      <button
                        className="text-xs text-tertiary hover:text-secondary transition-colors cursor-pointer"
                        disabled={portalMutation.isPending}
                        onClick={async () => {
                          if (!workspaceId) return;
                          try {
                            const result = await portalMutation.mutateAsync({
                              workspaceId,
                            });
                            if (result.portalUrl) {
                              window.location.href = result.portalUrl;
                            }
                          } catch (e) {
                            toast({
                              message:
                                e instanceof Error
                                  ? e.message
                                  : "Nie udało się otworzyć panelu.",
                              type: "error",
                            });
                          }
                        }}
                      >
                        {portalMutation.isPending ? "Ładowanie..." : "Zarządzaj →"}
                      </button>
                    )
                  )}
                </div>
              </>
            ) : (
              <>
                <SectionLabel>Subskrypcja</SectionLabel>
                <p className="text-sm text-tertiary">Brak subskrypcji</p>
              </>
            )}
          </GlassCard>

          {/* ═══ Top-up ═══ */}
          <GlassCard span>
            <SectionLabel>Doładuj konto</SectionLabel>
            {!hasStripeSubscription ? (
              <p className="text-sm text-tertiary">
                Doładowania dostępne po opłaceniu subskrypcji.
              </p>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4">
                  {TOPUP_OPTIONS.map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      onClick={() => {
                        setSelectedAmount(amount);
                      }}
                      className={clsx(
                        "py-3.5 rounded-xl text-lg font-semibold transition-all cursor-pointer",
                        selectedAmount === amount
                          ? "bg-accent text-white shadow-glow scale-[1.02]"
                          : "bg-surface-hover/80 text-secondary hover:text-primary hover:bg-surface-hover",
                      )}
                    >
                      ${amount}
                    </button>
                  ))}
                </div>
                <Button
                  onClick={handleTopup}
                  isLoading={topupMutation.isPending}
                  disabled={!workspaceId}
                >
                  <span className="flex items-center justify-center gap-2">
                    <CreditCardIcon className="size-4" />
                    Zapłać ${selectedAmount}
                  </span>
                </Button>
              </>
            )}
          </GlassCard>

          {/* ═══ Usage: Daily ═══ */}
          {isUsagePending ? (
            <>
              <GlassCard>
                <SkeletonLine className="h-3 w-20 mb-6" />
                <SkeletonLine className="h-14 w-14 rounded-full" />
              </GlassCard>
              <GlassCard>
                <SkeletonLine className="h-3 w-20 mb-6" />
                <SkeletonLine className="h-14 w-14 rounded-full" />
              </GlassCard>
            </>
          ) : isUsageError ? (
            <GlassCard span>
              <p className="text-sm text-red-500">
                Nie udało się pobrać limitów.
              </p>
            </GlassCard>
          ) : usageData ? (
            <>
              <GlassCard>
                <SectionLabel>Dzienny limit</SectionLabel>
                <div className="flex items-center gap-4">
                  <CircularProgress pct={dailyPct} isHigh={dailyPct >= 80} />
                  <div>
                    <p className="text-[22px] font-bold text-primary tabular-nums leading-none">
                      {usageData.usage.daily}
                      <span className="text-sm font-normal text-tertiary">
                        {" "}/ {usageData.limits.daily}
                      </span>
                    </p>
                    <p className="text-[10px] text-tertiary mt-1">generacji</p>
                  </div>
                </div>
              </GlassCard>

              <GlassCard>
                <SectionLabel>Tygodniowy limit</SectionLabel>
                <div className="flex items-center gap-4">
                  <CircularProgress
                    pct={weeklyPct}
                    isHigh={weeklyPct >= 80}
                  />
                  <div>
                    <p className="text-[22px] font-bold text-primary tabular-nums leading-none">
                      {usageData.usage.weekly}
                      <span className="text-sm font-normal text-tertiary">
                        {" "}/ {usageData.limits.weekly}
                      </span>
                    </p>
                    <p className="text-[10px] text-tertiary mt-1">generacji</p>
                  </div>
                </div>
              </GlassCard>
            </>
          ) : null}

          {/* ═══ Storage ═══ */}
          <GlassCard span>
            {isStoragePending ? (
              <>
                <SkeletonLine className="h-3 w-28 mb-6" />
                <SkeletonLine className="h-8 w-40 mb-3" />
                <SkeletonLine className="h-1 w-full" />
              </>
            ) : isStorageError ? (
              <p className="text-sm text-red-500">
                Nie udało się pobrać danych CDN.
              </p>
            ) : storageData ? (
              <>
                <SectionLabel>Wykorzystanie CDN</SectionLabel>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="text-[28px] font-bold text-primary tabular-nums leading-none">
                    {formatStorageSize(storageData.storage.total.totalMB)}
                  </span>
                  {storageLimitMB !== null && (
                    <span className="text-sm text-tertiary">
                      / {formatStorageSize(storageLimitMB)}
                    </span>
                  )}
                  <span className="text-[10px] text-tertiary ml-1">
                    ({storageData.storage.total.fileCount} plików)
                  </span>
                </div>

                {storageLimitMB !== null && (
                  <ProgressBar pct={storagePct} isHigh={storagePct >= 80} />
                )}

                {storageData.storage.byType.length > 0 && (
                  <div className="flex gap-2 mt-4">
                    {storageData.storage.byType.map((item) => (
                      <div
                        key={item.fileType}
                        className="flex-1 bg-surface-hover/60 rounded-xl p-3"
                      >
                        <p className="text-[10px] font-medium uppercase tracking-[1px] text-tertiary">
                          {item.fileType === "image" ? "Obrazy" : "Wideo"}
                        </p>
                        <p className="text-lg font-semibold text-primary tabular-nums mt-1">
                          {formatStorageSize(item.totalMB)}
                        </p>
                        <p className="text-[10px] text-tertiary">
                          {item.fileCount} plików
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <SectionLabel>Wykorzystanie CDN</SectionLabel>
                <p className="text-sm text-tertiary">Brak danych</p>
              </>
            )}
          </GlassCard>

          {/* ═══ Transactions ═══ */}
          {isPending ? (
            <GlassCard span>
              <SkeletonLine className="h-3 w-36 mb-6" />
              <div className="space-y-3">
                <SkeletonLine className="h-10 w-full" />
                <SkeletonLine className="h-10 w-full" />
                <SkeletonLine className="h-10 w-full" />
              </div>
            </GlassCard>
          ) : data && data.recentTransactions.length > 0 ? (
            <GlassCard span className="!p-0 overflow-hidden">
              <div className="px-5 sm:px-6 pt-5 sm:pt-6">
                <SectionLabel>Ostatnie transakcje</SectionLabel>
              </div>
              <div>
                {data.recentTransactions.map((tx, i) => (
                  <div
                    key={tx.id}
                    className={clsx(
                      "flex items-center justify-between px-5 sm:px-6 py-3",
                      i % 2 === 1 && "bg-surface-hover/30",
                    )}
                  >
                    <div className="min-w-0">
                      <p className="text-sm text-primary truncate">
                        {tx.description}
                      </p>
                      <p className="text-[10px] text-tertiary tabular-nums mt-0.5">
                        {new Date(tx.createdAt).toLocaleDateString("pl-PL", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                    <span
                      className={clsx(
                        "font-semibold tabular-nums text-sm shrink-0 ml-4",
                        tx.amountCents >= 0
                          ? "text-green-500"
                          : "text-red-400",
                      )}
                    >
                      {tx.amountCents >= 0 ? "+" : "-"}$
                      {formatCents(Math.abs(tx.amountCents))}
                    </span>
                  </div>
                ))}
              </div>
            </GlassCard>
          ) : null}
        </div>
      </div>
    </BasicLayout>
  );
}
